import { useLocation } from 'react-router-dom';
import isEmpty from 'lodash/fp/isEmpty';

import { Carousel, Loading } from '@autopass-web/mochi-component';
import { SuccessPaymentModule } from '@autopass-web/mochi-payment';

import { filterAndSortPromotions } from '../search-plate-number-page/filter-and-sort-promotions';
import styles from './index.module.scss';

import type { ParkingOrder, PaymentMethodType } from '@autopass-web/types';

import { PageContainer } from '#/guest-aps/components/page-container';
import { useAmplitudeSuccessPageview } from '#/guest-aps/hooks/use-amplitude-success-pageview';
import amplitude from '#/guest-aps/services/amplitude';
import { sessionStorageController } from '#/guest-aps/services/storages';
import { useBulletinBoards } from '#/guest-aps/services/use-bulletin-boards';
import { usePoi } from '#/guest-aps/services/use-poi';
import {
  createParkinglotCodeReplacer,
  createVehicleInfoReplacer,
} from '#/guest-aps/utils/bulletin-replacer';
import { createCarouselPromotionList } from '#/guest-aps/utils/create-carousel-promotion-list';

/**
 * - pull resources type up to a single project
 * - pull type utils up to a single project
 */

type LocationState = { paymentType: PaymentMethodType; data: ParkingOrder };

export const SuccessPage = () => {
  const vehicleType = sessionStorageController.getVehicleType();
  const location = useLocation();
  const { data: parkingOrder } = location.state as LocationState;

  const {
    parkinglot_name,
    should_leave_before,
    discount_amount,
    amount,
    email,
    parking_ticket_id,
    plate_number,
  } = parkingOrder;

  useAmplitudeSuccessPageview({
    ticketId: parking_ticket_id,
    plateNumber: plate_number,
  });

  const { parkinglot, getPoiStatus } = usePoi(
    { poiId: (location.state as LocationState).data.parkinglot_id },
    { enabled: !isEmpty(location.state) },
  );

  const {
    bulletinBoards,
    getBulletinBoardsStatus,
    apsBackgroundStyles,
    apsPartnerTemplate,
  } = useBulletinBoards(
    {
      codes:
        'aps_promotion, aps_partner_template, alpr_success_to_aps_with_info',
      partnerId: parkinglot?.partner_id,
    },
    { enabled: !!parkinglot?.partner_id },
  );

  if (getPoiStatus === 'pending' || getBulletinBoardsStatus === 'pending') {
    return (
      <div className={styles.loading}>
        <Loading inline />
        <div>交易處理中，請稍候</div>
        <div style={{ fontSize: 14, marginTop: 6, width: '70%' }}>
          為了確保交易成功，請勿重新整理或關閉頁面。
        </div>
      </div>
    );
  }

  const promotions = filterAndSortPromotions(
    bulletinBoards?.alpr_success_to_aps_with_info ?? [],
  )
    .map(createParkinglotCodeReplacer(parkinglot))
    .map(createVehicleInfoReplacer(parkingOrder.plate_number, vehicleType));

  const bulletin = (() => {
    return (
      <Carousel
        loading={!bulletinBoards}
        items={createCarouselPromotionList(
          {
            promotions: promotions,
          },
          {
            onClickFactory: (bulletin) => {
              return () => {
                amplitude.successBulletinOnClick({
                  parkinglotId: parkinglot.id,
                  parkinglotName: parkinglot.name,
                  bulletin_board_code: 'alpr_success_to_aps_with_info',
                  bulletin_id: bulletin.id,
                });
              };
            },
          },
        )}
        indicator={false}
      />
    );
  })();

  return (
    <PageContainer style={apsBackgroundStyles} invertFooter>
      <SuccessPaymentModule
        name={parkinglot_name}
        email={email}
        shouldLeaveBefore={should_leave_before}
        amount={amount}
        discountAmount={discount_amount}
        isApsPartner={!!apsPartnerTemplate}
        bulletin={bulletin}
      />
    </PageContainer>
  );
};
